import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getSkippedSenders, getDuplicateCount } from "@/lib/apiClient";
import { NavLink, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Building2 as OrgIcon, CreditCard, UserCog, FileBarChart2, Copy } from "lucide-react";
import { MobileTabBar } from "@/components/MobileTabBar";
import { LegalFooter } from "@/components/LegalFooter";
import { UsageWarningBanner } from "@/components/UsageWarningBanner";
import { TrialBanner, ProfileDialog } from "@/components/AccountBits";
import { SyncHealthBadge } from "@/components/SyncHealthBadge";
import {
  LayoutDashboard,
  UploadCloud,
  ListChecks,
  ScrollText,
  SlidersHorizontal,
  Building2,
  Landmark,
  CalendarRange,
  Hexagon,
  Moon,
  Sun,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard" },
  { to: "/upload", label: "Upload & Ingest", icon: UploadCloud, testId: "nav-upload" },
  { to: "/review", label: "Review Queue", icon: ListChecks, testId: "nav-review" },
  { to: "/suppliers", label: "Suppliers", icon: Building2, testId: "nav-suppliers" },
  { to: "/payments", label: "Payment Runs", icon: Landmark, testId: "nav-payments" },
  { to: "/my-week", label: "My Week", icon: CalendarRange, testId: "nav-my-week" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testId: "nav-audit" },
  { to: "/settings", label: "Rules & Accounts", icon: SlidersHorizontal, testId: "nav-settings" },
];

const initials = (name) =>
  name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export const Layout = ({ children }) => {
  const { theme, toggleTheme, user, isAdmin, authUser } = useApp();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const nav = NAV.filter((n) => n.to !== "/settings" || isAdmin);
  const location = useLocation();
  const [skipped, setSkipped] = useState(0);
  const [dups, setDups] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const focusMode = location.pathname === "/approve";

  useEffect(() => {
    const load = () => {
      getSkippedSenders().then((r) => setSkipped(r.total || 0)).catch(() => {});
      getDuplicateCount().then((r) => setDups(r.count || 0)).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background">
      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
      <header className="sticky top-0 z-40 border-b border-border backdrop-blur-xl bg-background/85">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <button
              data-testid="brand-logo"
              onClick={() => navigate("/")}
              className="flex items-center gap-2.5 group"
            >
              <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Hexagon className="h-5 w-5" strokeWidth={2.4} />
                <span className="absolute text-[10px] font-bold font-mono">20</span>
              </span>
              <div className="text-left leading-none">
                <div className="font-heading text-lg font-extrabold tracking-tight">D20</div>
                <div className="text-[10px] text-muted-foreground tracking-wide">INVOICE AI</div>
              </div>
            </button>

            <nav className="hidden md:flex items-center gap-1">
              {nav.map(({ to, label, icon: Icon, end, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={testId}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  {to === "/review" && dups > 0 && (
                    <span data-testid="nav-duplicates-pill" role="button" title={`${dups} possible duplicate(s) waiting for a decision — click to review`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/review?filter=duplicates"); }}
                          className="ml-0.5 inline-flex items-center gap-0.5 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-mono text-amber-600 dark:text-amber-400 hover:bg-amber-500/30"><Copy className="h-2.5 w-2.5" />{dups}</span>
                  )}
                  {to === "/my-week" && skipped > 0 && (
                    <span data-testid="nav-skipped-pill" title={`${skipped} skipped sender(s) need a decision`} className="ml-0.5 rounded-full bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-mono text-amber-500">{skipped}</span>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <SyncHealthBadge />
              <button
                data-testid="theme-toggle"
                onClick={toggleTheme}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? <Sun className="h-4.5 w-4.5" /> : <Moon className="h-4.5 w-4.5" />}
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu" className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-muted transition-colors">
                    <Avatar className="h-8 w-8 border border-border">
                      <AvatarFallback className="bg-primary/15 text-primary text-xs font-semibold">
                        {initials(user || "?")}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium">{user}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm font-medium" data-testid="user-menu-name">{authUser?.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{authUser?.email}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><OrgIcon className="h-3 w-3" /> <span data-testid="user-menu-org">{authUser?.org?.name}</span> · <span className="capitalize" data-testid="user-menu-role">{authUser?.role}</span></div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="user-menu-profile" onClick={() => setProfileOpen(true)}><UserCog className="h-4 w-4 mr-2" /> Profile & password</DropdownMenuItem>
                  {isAdmin && <DropdownMenuItem data-testid="user-menu-team" onClick={() => navigate("/settings?tab=team")}>Team & workspace</DropdownMenuItem>}
                  {isAdmin && <DropdownMenuItem data-testid="user-menu-billing" onClick={() => navigate("/billing")}><CreditCard className="h-4 w-4 mr-2" /> Billing & plan</DropdownMenuItem>}
                  <DropdownMenuItem data-testid="user-menu-logout" onClick={() => logout().then(() => navigate("/login"))} className="text-red-500 focus:text-red-500"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

      </header>
      <TrialBanner />
      <UsageWarningBanner />

      <main className={cn("mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 lg:py-8", !focusMode && "md:pb-8")}>{children}</main>
      <LegalFooter className={cn("mt-auto", !focusMode && "mb-20 md:mb-0")} />
      {!focusMode && <MobileTabBar more={nav.filter((n) => !["/", "/review", "/upload"].includes(n.to))} dups={dups} skipped={skipped} isAdmin={isAdmin}
                     onProfile={() => setProfileOpen(true)} onBilling={() => navigate("/billing")} onLogout={() => logout().then(() => navigate("/login"))} />}
    </div>
  );
};
